import React, { useState, useMemo } from 'react';
import { Box, Container } from '@mui/material';
import TabNavigation from './TabNavigation';
import CalendarTab from './CalendarTab';
import PayrollTab from './PayrollTab';
import EmployeeTab from './EmployeeTab';
import HrRecordsTab from './HrRecordsTab';
import BusinessScaleCard from './BusinessScaleCard';
import { EmployeeManagementProps, EmployeeManagementTab } from '../../types/employee';
import { useEmployeeData, useBusinessScaleOverride, useLaborDayToggle } from '../../hooks';
import { useAccountBookStore } from '../../store/accountBookStore';
import {
  getActiveEmployeeCountForMonth,
  calculate5PlusForLaborDay,
} from '../../utils/businessScale';
import { useCompanySizeThreshold } from '../../hooks/useRates';

const EmployeeManagement: React.FC<EmployeeManagementProps> = ({ initialTab }) => {
  const [activeTab, setActiveTab] = useState<EmployeeManagementTab>(initialTab ?? 'calendar');
  const [selectedMonth, setSelectedMonth] = useState<Date>(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const { employees } = useEmployeeData();
  const userId = useAccountBookStore((s) => s.userId);
  const currentAccountNumber = useAccountBookStore((s) => s.currentAccountNumber);
  const currentAccountBook = useAccountBookStore((s) => s.currentAccountBook);
  const accountNumber = currentAccountNumber ?? currentAccountBook?.accountNumber ?? 1;

  const { isBusinessScaleOverrideUnder5, setOverride } = useBusinessScaleOverride(
    userId ?? null,
    accountNumber
  );

  const laborDayToggle = useLaborDayToggle(userId ?? null, accountNumber);

  const companySizeThreshold = useCompanySizeThreshold();
  const activeCount = useMemo(
    () => getActiveEmployeeCountForMonth(employees, selectedMonth, accountNumber),
    [employees, selectedMonth, accountNumber]
  );
  const isActualCount5Plus = activeCount >= companySizeThreshold;
  const isBusinessScale5Plus = isBusinessScaleOverrideUnder5 ? false : isActualCount5Plus;

  // 노동절 가산 5인 이상 판정 — 전월 말일 기준 (설계문서 §7).
  // scale_override === 'under5' 일 때는 강제 false.
  const laborDayIs5Plus = useMemo(() => {
    if (isBusinessScaleOverrideUnder5) return false;
    return calculate5PlusForLaborDay(employees, selectedMonth, accountNumber);
  }, [isBusinessScaleOverrideUnder5, employees, selectedMonth, accountNumber]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'calendar':
        return <CalendarTab />;
      case 'payroll':
        return (
          <PayrollTab
            selectedMonth={selectedMonth}
            onMonthChange={setSelectedMonth}
            isBusinessScale5Plus={isBusinessScale5Plus}
            laborDayIs5Plus={laborDayIs5Plus}
            laborDayToggle={laborDayToggle}
            laborDaySyncDisabled={!userId}
          />
        );
      case 'employees':
        return <EmployeeTab />;
      case 'records':
        return (
          <HrRecordsTab
            selectedMonth={selectedMonth}
            onMonthChange={setSelectedMonth}
          />
        );
      default:
        return <CalendarTab />;
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: 'var(--bg-elev-2)',
    }}>
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <TabNavigation
          activeTab={activeTab}
          onTabChange={setActiveTab}
          employees={employees}
          selectedMonth={selectedMonth}
          accountNumber={accountNumber}
          isBusinessScale5Plus={isBusinessScale5Plus}
        />

        <BusinessScaleCard
          employees={employees}
          selectedMonth={selectedMonth}
          accountNumber={accountNumber}
          isBusinessScale5Plus={isBusinessScale5Plus}
          isOverrideUnder5={isBusinessScaleOverrideUnder5}
          onSetOverride={setOverride}
        />

        <Box sx={{ 
          background: '#ffffff',
          borderRadius: 1,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          border: '1px solid var(--border-default)',
          overflow: 'hidden',
        }}>
          {renderTabContent()}
        </Box>
      </Container>
    </Box>
  );
};

export default EmployeeManagement;
